import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import { Check, ShoppingCart, Star, Loader2 } from 'lucide-react';
import { products } from '../data/mock';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductsPage = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const handleBuyNow = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
    setPaymentSuccess(false);
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
      alert('Please fill in all customer details');
      return;
    }

    setIsLoading(true);

    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        alert('Failed to load payment gateway. Please try again.');
        setIsLoading(false);
        return;
      }

      // Calculate amount in paise
      const amountInPaise = (selectedProduct.priceWithGst || selectedProduct.price) * 100;

      // Create order on backend
      const response = await fetch(`${API_URL}/api/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: amountInPaise,
          product_id: selectedProduct.id.toString(),
          product_name: selectedProduct.name,
          customer_name: customerInfo.name,
          customer_email: customerInfo.email,
          customer_phone: customerInfo.phone
        }),
      });

      const orderData = await response.json();

      if (!orderData.success) {
        throw new Error('Failed to create order');
      }

      // Open Razorpay checkout
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'STEAM-IE',
        description: selectedProduct.name,
        order_id: orderData.order_id,
        handler: async function (response) {
          // Verify payment on backend
          try {
            const verifyResponse = await fetch(`${API_URL}/api/verify-payment`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              }),
            });

            const verifyData = await verifyResponse.json();

            if (verifyData.success) {
              setPaymentSuccess(true);
            } else {
              alert('Payment verification failed. Please contact support.');
            }
          } catch (error) {
            console.error('Verification error:', error);
            alert('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: customerInfo.name,
          email: customerInfo.email,
          contact: customerInfo.phone
        },
        theme: {
          color: '#2563eb'
        },
        modal: {
          ondismiss: function() {
            setIsLoading(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      setIsLoading(false);

    } catch (error) {
      console.error('Payment error:', error);
      alert('Failed to initiate payment. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-20 bg-gradient-to-br from-orange-500 via-red-500 to-pink-500 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1.5 bg-white/20 text-white text-sm font-semibold rounded-full mb-4 md:mb-6 backdrop-blur-sm border border-white/30">
            STEAM-IE Kits
          </span>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
            Premium Robotics & Coding Kits
          </h1>
          <p className="text-base md:text-xl text-white/90 max-w-3xl mx-auto px-4">
            Get a coding kit delivered at your doorstep that introduces your child 
            to the exciting world of coding and robotics.
          </p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {products.map((product) => (
              <div
                key={product.id}
                className="group bg-white rounded-2xl md:rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                {/* Image */}
                <div className="relative h-48 md:h-64 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  
                  {/* Badge */}
                  <Badge className={`absolute top-3 right-3 md:top-4 md:right-4 ${
                    product.badge === 'Bestseller' 
                      ? 'bg-green-500' 
                      : product.badge === '50% OFF'
                      ? 'bg-red-500'
                      : 'bg-blue-500'
                  } text-white px-2 md:px-3 py-1 text-xs md:text-sm`}>
                    {product.badge}
                  </Badge>

                  {/* Grade Badge */}
                  <div className="absolute bottom-3 left-3 md:bottom-4 md:left-4">
                    <span className="bg-white/90 backdrop-blur-sm text-gray-800 px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium">
                      {product.grade}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 md:p-6">
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-gray-600 text-xs md:text-sm mb-3 md:mb-4">
                    {product.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-1.5 md:space-y-2 mb-4 md:mb-6">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-xs md:text-sm text-gray-600">
                        <Check className="w-3 h-3 md:w-4 md:h-4 text-green-500 mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Price */}
                  <div className="mb-3 md:mb-4">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-xl md:text-2xl font-bold text-gray-900">
                        ₹{product.price.toLocaleString()}
                      </span>
                      {product.gst && (
                        <span className="text-xs md:text-sm text-gray-500">+ {product.gst}% GST</span>
                      )}
                    </div>
                    {product.priceWithGst && (
                      <p className="text-xs md:text-sm text-green-600 font-medium">
                        Total: ₹{product.priceWithGst.toLocaleString()} (incl. GST)
                      </p>
                    )}
                    {product.originalPrice && (
                      <span className="text-gray-400 line-through text-xs md:text-sm">
                        ₹{product.originalPrice.toLocaleString()}
                      </span>
                    )}
                    <div className="flex items-center text-yellow-500 mt-2">
                      <Star className="w-3 h-3 md:w-4 md:h-4 fill-current" />
                      <Star className="w-3 h-3 md:w-4 md:h-4 fill-current" />
                      <Star className="w-3 h-3 md:w-4 md:h-4 fill-current" />
                      <Star className="w-3 h-3 md:w-4 md:h-4 fill-current" />
                      <Star className="w-3 h-3 md:w-4 md:h-4 fill-current" />
                    </div>
                  </div>

                  {/* CTA */}
                  <Button 
                    onClick={() => handleBuyNow(product)}
                    className="w-full bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white rounded-xl py-2.5 md:py-3 group/btn text-sm md:text-base"
                    data-testid={`buy-now-${product.id}`}
                  >
                    <ShoppingCart className="w-4 h-4 mr-2 group-hover/btn:scale-110 transition-transform" />
                    Buy Now
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why Choose STEAM-IE Kits?
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">
              Our kits are designed by experts with 20+ years of experience in STEM education.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {[
              { title: 'Expert Designed', desc: 'Created by industry experts with decades of experience' },
              { title: 'Age Appropriate', desc: 'Carefully curated content for different age groups' },
              { title: 'Certification', desc: 'Official STEAM-IE certification upon completion' },
              { title: 'Ongoing Support', desc: '24/7 online support and video tutorials' }
            ].map((item, index) => (
              <div key={index} className="text-center p-4 md:p-6 rounded-xl md:rounded-2xl bg-gradient-to-br from-blue-50 to-teal-50 hover:shadow-lg transition-shadow">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-blue-600 to-teal-500 rounded-lg md:rounded-xl flex items-center justify-center mx-auto mb-3 md:mb-4 text-white font-bold text-sm md:text-base">
                  {index + 1}
                </div>
                <h3 className="font-semibold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">{item.title}</h3>
                <p className="text-xs md:text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Purchase Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {paymentSuccess ? 'Payment Successful!' : `Purchase ${selectedProduct?.name}`}
            </DialogTitle>
            <DialogDescription>
              {paymentSuccess 
                ? 'Thank you for your purchase. You will receive a confirmation email shortly.'
                : 'Fill in your details to proceed with secure payment.'
              }
            </DialogDescription>
          </DialogHeader>
          
          {paymentSuccess ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-gray-600 mb-4">Your order has been placed successfully!</p>
              <Button 
                onClick={() => {
                  setShowModal(false);
                  setPaymentSuccess(false);
                  setCustomerInfo({ name: '', email: '', phone: '' });
                }}
                className="bg-gradient-to-r from-blue-600 to-teal-500 text-white"
              >
                Continue Shopping
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Customer Details */}
              <div className="space-y-3">
                <div>
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    placeholder="Enter your full name"
                    value={customerInfo.name}
                    onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={customerInfo.email}
                    onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    placeholder="Enter your phone number"
                    value={customerInfo.phone}
                    onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                  />
                </div>
              </div>

              {/* Order Summary */}
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Product Price</span>
                  <span className="font-semibold">₹{selectedProduct?.price?.toLocaleString()}</span>
                </div>
                {selectedProduct?.gst && (
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">GST ({selectedProduct?.gst}%)</span>
                    <span className="font-semibold">₹{((selectedProduct?.price * selectedProduct?.gst) / 100).toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Shipping</span>
                  <span className="text-green-600">FREE</span>
                </div>
                <div className="border-t pt-2 mt-2 flex justify-between items-center">
                  <span className="font-semibold">Total</span>
                  <span className="font-bold text-xl">₹{(selectedProduct?.priceWithGst || selectedProduct?.price)?.toLocaleString()}</span>
                </div>
              </div>

              <Button 
                onClick={handlePayment}
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-blue-600 to-teal-500 hover:from-blue-700 hover:to-teal-600 text-white py-3"
                data-testid="proceed-to-payment"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Proceed to Payment'
                )}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                Secure payment powered by Razorpay
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default ProductsPage;
